import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Checkbox,
  Flex,
  Text,
  VStack,
} from "@chakra-ui/react";
import { reachCount } from "../../utils/confirmationChannels";

// "Text, email, or both?" before the Admin page's Send Confirmations sends
// to the selected patients. A channel nobody selected can use is greyed out,
// with the reason underneath. `summary` comes from
// summarizeConfirmationChannels; onChoose(choice, { includeBad }).

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const ConfirmationChannelDialog = ({ isOpen, onClose, summary, onChoose }) => {
  const [includeBad, setIncludeBad] = useState(false);
  useEffect(() => {
    if (isOpen) setIncludeBad(false);
  }, [isOpen]);

  if (!summary) return null;
  const opts = { includeBad };
  const textCount = summary.text.length + (includeBad ? summary.badNumber.length : 0);
  const emailCount = summary.email.length;
  const options = [
    {
      key: "sms",
      label: "Text (SMS)",
      count: reachCount(summary, "sms", opts),
      disabled: textCount === 0,
      why: "No selected patient has a working phone number.",
    },
    {
      key: "email",
      label: "Email",
      count: reachCount(summary, "email", opts),
      disabled: emailCount === 0,
      why: "No selected patient has an email address.",
    },
    {
      key: "both",
      label: "Both",
      count: reachCount(summary, "both", opts),
      disabled: textCount === 0 || emailCount === 0,
      why: "Needs at least one patient who can be texted and one who can be emailed.",
    },
  ];
  const open = summary.total - summary.answered;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent data-testid="confirmation-channel-dialog">
        <ModalHeader>Send confirmations</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={1} fontSize="sm" mb={4} data-testid="confirmation-channel-summary">
            <Text>{plural(open, "selected patient")} still to confirm.</Text>
            <Text color="gray.600">
              {plural(summary.text.length, "patient")} with a working phone number ·{" "}
              {emailCount} email address{emailCount === 1 ? "" : "es"}
            </Text>
            {summary.badNumber.length > 0 && (
              <Text color="orange.600">
                {plural(summary.badNumber.length, "known bad number")}: every number on file for{" "}
                {summary.badNumber.length === 1 ? "that patient" : "those patients"} has failed.
              </Text>
            )}
            {summary.unreachable.length > 0 && (
              <Text color="orange.600">{plural(summary.unreachable.length, "patient")} with no working phone and no email will be skipped.</Text>
            )}
            {summary.answered > 0 && (
              <Text color="gray.500">{summary.answered} already answered, skipped.</Text>
            )}
          </VStack>
          <VStack align="stretch" spacing={3}>
            {options.map((o) => (
              <VStack key={o.key} align="stretch" spacing={0}>
                <Button
                  colorScheme={o.key === "email" ? "teal" : "blue"}
                  variant={o.key === "both" ? "solid" : "outline"}
                  isDisabled={o.disabled}
                  onClick={() => onChoose(o.key, { includeBad })}
                  data-testid={`confirmation-channel-${o.key}`}
                  justifyContent="space-between"
                >
                  <span>{o.label}</span>
                  <span>{o.disabled ? "—" : plural(o.count, "patient")}</span>
                </Button>
                {o.disabled && (
                  <Text fontSize="xs" color="gray.500" mt={1}>{o.why}</Text>
                )}
              </VStack>
            ))}
          </VStack>
          {summary.badNumber.length > 0 && (
            <Checkbox mt={4} isChecked={includeBad} onChange={(e) => setIncludeBad(e.target.checked)}
                      data-testid="confirmation-channel-include-bad">
              <Text fontSize="sm">Text the known bad number{summary.badNumber.length === 1 ? "" : "s"} anyway (every number on file for {summary.badNumber.length === 1 ? "this patient" : "these patients"} has failed)</Text>
            </Checkbox>
          )}
        </ModalBody>
        <ModalFooter>
          <Flex justify="flex-end" w="100%">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConfirmationChannelDialog;
